import React, { useEffect, useRef } from 'react';
import { chipInLine, CHIP_IN_TOAST_MS } from '../../utils/chipIn';

// 🐻 The thank-you toast (SPACES_REDESIGN_BRAINSTORM_2026_09_29 §3.6):
// "🐻 A mysterious bear chipped in. The AI Dorm stays on."
// The go-live toast's pattern (GameStepPage `liveToast`): a fixed top toast, one
// at a time, 8s, a ✕. It only ever shows what the socket event says (an animal
// key); the page holds the queue (utils/chipIn pushChipIn / shiftChipIn) and
// hands the head in as `item`. Page-only: nothing here reaches a resident.
// The timer pauses while the pointer or focus is on the toast (so a reader can
// finish it), and reduced motion drops the slide.
// 💳 10-29 (the Chip in test run): Chip in opens Stripe in a NEW tab, so the payer's
// own thank-you ran its 8 seconds behind Stripe's page and was gone when they came
// back (the TEST tip reached the room at 03:34:26Z; Nina saw nothing). The clock now
// runs only while the page is in view: a toast that lands in a hidden tab waits and
// starts its 8 seconds when the tab comes back.
// Armor: generated room themes style EVERY button (some with !important) and
// any class holding card/panel/chip/message/btn/badge/tag/user/player/header,
// so the class prefix is `cit-` (never "chip"), the ✕ wears data-prop-ui plus
// doubled-class !important rules, and the critical layout is inline.
// ⚠️ No global box-sizing here: every padded box sets it.
const CSS = `
.cit-wrap, .cit-wrap * { box-sizing: border-box; }
.cit-wrap { animation: citIn .22s cubic-bezier(.2,.8,.2,1); }
.cit-wrap .cit-x.cit-x { all: unset; box-sizing: border-box; display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; flex: none; border-radius: 8px !important; padding: 0 !important; margin: 0 !important; border: 0 !important; background: transparent !important; color: rgba(236,238,242,0.62) !important; font: 600 14px/1 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif !important; cursor: pointer; box-shadow: none !important; text-shadow: none !important; transform: none !important; min-width: 0 !important; min-height: 0 !important; text-transform: none !important; letter-spacing: 0 !important; }
.cit-wrap .cit-x.cit-x:hover { background: rgba(236,238,242,0.1) !important; color: #ECEEF2 !important; }
.cit-wrap .cit-x:focus-visible { outline: 2px solid #5CE1E6 !important; outline-offset: 2px; }
@keyframes citIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .cit-wrap { animation: none; } }
`;

export default function ChipInToast({ item, roomName, onDone, top = 18 }) {
  const timer = useRef(null);
  const left = useRef(CHIP_IN_TOAST_MS);
  const started = useRef(0);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  const id = item ? item.id : null;
  const idRef = useRef(id);
  idRef.current = id;

  const stop = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  const start = () => {
    stop();
    started.current = Date.now();
    timer.current = setTimeout(() => { timer.current = null; if (doneRef.current) doneRef.current(); }, Math.max(0, left.current));
  };
  // what holds the clock: 'pointer' · 'focus' · 'hidden' (the tab is out of view); it runs with none
  const holds = useRef(new Set());
  const pageHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
  const pause = (why) => {
    holds.current.add(why);
    if (!timer.current) return;
    stop();
    left.current = Math.max(1000, left.current - (Date.now() - started.current));
  };
  const resume = (why) => {
    holds.current.delete(why);
    if (!holds.current.size && !timer.current && idRef.current) start();
  };

  useEffect(() => {
    if (!id) return undefined;
    left.current = CHIP_IN_TOAST_MS;
    holds.current = new Set(pageHidden() ? ['hidden'] : []);
    if (!holds.current.size) start();
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onVisibility = () => { if (pageHidden()) pause('hidden'); else resume('hidden'); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!item) return null;
  const line = chipInLine(item.animal, roomName);
  return (
    <div
      className="cit-wrap"
      role="status"
      aria-live="polite"
      data-chip-in-toast={item.animal}
      onMouseEnter={() => pause('pointer')}
      onMouseLeave={() => resume('pointer')}
      onFocus={() => pause('focus')}
      onBlur={() => resume('focus')}
      style={{
        position: 'fixed',
        top: `calc(${top}px + env(safe-area-inset-top, 0px))`,
        left: 0, right: 0, marginLeft: 'auto', marginRight: 'auto',
        // review 09-29: the music FAB (LofiRadio, z 100010, top-right: phones 34px at 10/10,
        // the desktop game page 48px at right 200) sat OVER this toast's ✕ (measured in headless
        // Chrome: a 900px-wide game page put the whole ✕ under the FAB, and at 390px a two-line
        // toast lost its ✕'s top-right corner to it; a tap there started the music). The toast now
        // rides one layer above the FAB (it is 8s and dismissible; the ⋯ menus at 100020 and
        // the sheets at 150000+ stay above it), and on phones keeps 48px clear of each edge
        // so it never sits under the FAB at all.
        width: 'max-content', maxWidth: 'min(480px, calc(100vw - 96px))',
        zIndex: 100011,
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 10px 10px 14px', borderRadius: 12,
        background: 'rgba(18,21,26,0.96)',
        border: '1px solid rgba(76,175,80,0.55)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.45), 0 0 16px rgba(76,175,80,0.22)',
        color: '#ECEEF2', textAlign: 'left',
        font: '600 14px/1.35 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
        letterSpacing: 'normal', textTransform: 'none', textShadow: 'none',
      }}
    >
      <span aria-hidden="true" style={{ fontSize: 20, lineHeight: 1, flex: 'none' }}>{line.emoji}</span>
      <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{line.text}</span>
      <button
        type="button"
        className="cit-x"
        data-prop-ui=""
        aria-label="Dismiss"
        onClick={() => { stop(); if (onDone) onDone(); }}
      >
        ✕
      </button>
      <style>{CSS}</style>
    </div>
  );
}
